import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { styleSheet } from './styles';

interface DonationsByCampaignChartProps {
	data?: Api.IDonationByCampaignDataPoint[];
	isLoading?: boolean;
	className?: string;
}

const formatCurrencyAxisLabel = (value: number): string => {
	if (value >= 1000) {
		const thousands = value / 1000;
		// If it's a whole number, don't show decimals
		if (thousands % 1 === 0) {
			return `$${thousands}k`;
		}
		// Otherwise show one decimal place
		return `$${thousands.toFixed(1)}k`;
	}
	// For values less than 1000, show as whole number without cents
	return `$${Math.round(value)}`;
};

export const DonationsByCampaignChart: React.FC<DonationsByCampaignChartProps> = ({ data, isLoading, className }) => {
	const chartData = React.useMemo<Api.IDonationByCampaignDataPoint[]>(() => {
		if (!data || data.length === 0) {
			return [];
		}

		// Data is already sorted and limited by backend, just ensure colors are set
		return data.map(item => {
			const color = mainChartColor();
			return {
				...item,
				color,
			};
		});
	}, [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No donation data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{tooltipData.campaign}</p>
					<p className={css(styleSheet.tooltipValue)}>Amount: {formatCurrency(tooltipData.amount)}</p>
					<p className={css(styleSheet.tooltipValue)}>Count: {tooltipData.count}</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title)}>Donations by Campaign</h3>
			<ResponsiveContainer width='100%' height={400}>
				<BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='campaign'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						angle={-45}
						textAnchor='end'
						height={100}
					/>
					<YAxis
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						tickFormatter={formatCurrencyAxisLabel}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Bar dataKey='amount' radius={[4, 4, 0, 0]}>
						{chartData.map(item => {
							return <Cell key={item.campaign} fill={item.color} />;
						})}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
